import { useInfiniteQuery } from '@tanstack/react-query';
import * as React from 'react';
import { IPagedCollection } from '../../extViewmodels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { ISupportTicket } from '../../models/Support/ISupportTicket';
import { TicketPriority, TicketStatus } from '../../models/Support/SupportEnums';
import {
	SUPPORT_PATHS,
	SUPPORT_QUERY_KEYS,
	type SupportTicketFilterCriteria,
	type SupportTicketFilterOperator,
	SupportTicketFilterProperty,
	type SupportTicketFilterRequest,
	type SupportTicketListParams,
} from './supportQueryHelpers';

export interface SupportTicketsParams {
	filter?: string; // 'unassigned' | 'mine' | 'assigned' | 'closed' | 'spam' | 'archive' | 'priority' | 'all'
	status?: TicketStatus;
	priority?: TicketPriority;
	mailboxId: string;
	assigneeId?: string;
	contactEmail?: string;
	/** Case-insensitive "contains" match on the contact name or email (Contact header filter). */
	contact?: string;
	accountId?: string;
	categoryId?: string;
	/**
	 * CSM refinement. The server resolves it to that CSM's accounts — a ticket has no CSM of its own —
	 * so it narrows the current view like any other refinement instead of diverting to search.
	 */
	csmId?: string;
	/** Explicit sort override from the Filter & sort popover; falls back to the per-view default. */
	sort?: 'asc' | 'desc';
	sortBy?: string;
	/** One-based UI page; missing cursor pages are loaded sequentially. */
	page?: number;
	pageSize?: number;
	includeInactive?: boolean;
	/** Disable this query when another result source, such as ticket search, owns the inbox. */
	enabled?: boolean;
	/** Override the default polling interval (in ms). Pass `false` to disable polling. */
	refetchInterval?: number | false;
}

const UNASSIGNED_ASSIGNEE_VALUE = 'Unassigned';
const MISSING_CURRENT_USER_ASSIGNEE_VALUE = '__missing-current-user__';
// Hidden from the work queues and All tickets unless a status refinement asks for them. Snoozed is not here: a
// snoozed ticket stays in its queue, marked by its pill, so the agent can see what they parked.
const INACTIVE_STATUSES = [TicketStatus.Closed, TicketStatus.Spam, TicketStatus.Archive] as const;

function criterion(
	property: SupportTicketFilterProperty,
	value: string,
	op?: SupportTicketFilterOperator
): SupportTicketFilterCriteria {
	return op ? { property, value, op } : { property, value };
}

function statusCriterion(status: TicketStatus, op?: SupportTicketFilterOperator) {
	return criterion(SupportTicketFilterProperty.Status, status, op);
}

function activeStatusCriteria() {
	return INACTIVE_STATUSES.map(status => statusCriterion(status, 'Not'));
}

export function buildSupportTicketFilterRequest(
	params: SupportTicketsParams & { currentUserId?: string }
): SupportTicketFilterRequest {
	const criteria: SupportTicketFilterCriteria[] = [];
	const currentUserId = params.currentUserId;
	const hasExplicitStatus = !!params.status;
	const hasExplicitAssignee = !!params.assigneeId;

	if (params.mailboxId) {
		criteria.push(criterion(SupportTicketFilterProperty.Mailbox, params.mailboxId));
	}

	switch (params.filter) {
		case 'unassigned':
			if (!hasExplicitAssignee) {
				criteria.push(criterion(SupportTicketFilterProperty.Assignee, UNASSIGNED_ASSIGNEE_VALUE));
			}
			if (!hasExplicitStatus) criteria.push(...activeStatusCriteria());
			break;
		case 'mine':
			if (!hasExplicitAssignee) {
				criteria.push(
					criterion(SupportTicketFilterProperty.Assignee, currentUserId || MISSING_CURRENT_USER_ASSIGNEE_VALUE)
				);
			}
			if (!hasExplicitStatus) criteria.push(...activeStatusCriteria());
			break;
		case 'assigned':
			if (!hasExplicitAssignee) {
				criteria.push(criterion(SupportTicketFilterProperty.Assignee, UNASSIGNED_ASSIGNEE_VALUE, 'Not'));
				if (currentUserId) {
					criteria.push(criterion(SupportTicketFilterProperty.Assignee, currentUserId, 'Not'));
				}
			}
			if (!hasExplicitStatus) criteria.push(...activeStatusCriteria());
			break;
		case 'closed':
			if (!hasExplicitStatus) criteria.push(statusCriterion(TicketStatus.Closed));
			break;
		case 'spam':
			if (!hasExplicitStatus) criteria.push(statusCriterion(TicketStatus.Spam));
			break;
		case 'archive':
			if (!hasExplicitStatus) criteria.push(statusCriterion(TicketStatus.Archive));
			break;
		case 'priority':
		case 'all':
		default:
			if (!params.includeInactive && !hasExplicitStatus) {
				criteria.push(...activeStatusCriteria());
			}
			break;
	}

	if (params.status) {
		criteria.push(statusCriterion(params.status));
	}
	if (params.priority) {
		criteria.push(criterion(SupportTicketFilterProperty.Priority, params.priority));
	}
	if (params.assigneeId) {
		criteria.push(criterion(SupportTicketFilterProperty.Assignee, params.assigneeId));
	}
	if (params.contactEmail) {
		criteria.push(criterion(SupportTicketFilterProperty.ContactEmail, params.contactEmail));
	}
	if (params.contact) {
		criteria.push(criterion(SupportTicketFilterProperty.Contact, params.contact));
	}
	if (params.accountId) {
		criteria.push(criterion(SupportTicketFilterProperty.Account, params.accountId));
	}
	if (params.categoryId) {
		criteria.push(criterion(SupportTicketFilterProperty.Category, params.categoryId));
	}

	return params.csmId ? { criteria, csmId: params.csmId } : { criteria };
}

export function useSupportTicketsQuery(params: SupportTicketsParams) {
	const { filter, page = 1, pageSize = 25, refetchInterval } = params;
	const requestedPage = Number.isInteger(page) && page > 0 ? page : 1;
	const userSession = useUserSession();
	const filterRequest = buildSupportTicketFilterRequest({ ...params, currentUserId: userSession.user?.id });
	const queryParams: SupportTicketListParams = {
		filterRequest,
		pageSize,
		// An explicit popover sort wins; otherwise the "Priority" view keeps its priority ordering.
		sort: params.sort ?? (filter === 'priority' ? 'desc' : undefined),
		sortBy: params.sortBy ?? (filter === 'priority' ? 'Priority' : undefined),
	};
	const enabled = params.enabled !== false && !!params.mailboxId;
	const query = useInfiniteQuery<IPagedCollection<ISupportTicket>>({
		queryKey: SUPPORT_QUERY_KEYS.tickets.list(queryParams),
		initialPageParam: '',
		getNextPageParam: lastPage => lastPage.pageToken || undefined,
		staleTime: 30_000,
		refetchInterval,
		refetchOnWindowFocus: true,
		queryFn: ({ pageParam }) =>
			userSession.webServiceHelper.callAsync<IPagedCollection<ISupportTicket>>({
				path: SUPPORT_PATHS.tickets.list({
					...queryParams,
					pageToken: (pageParam as string) || undefined,
				}),
				method: 'POST',
				body: filterRequest,
			}),
		enabled,
		meta: { toastErrorMessage: 'Failed to load tickets' },
	});

	const { fetchNextPage } = query;
	const loadedPages = query.data?.pages;
	const loadedPageCount = loadedPages?.length ?? 0;
	const requestedPageData = loadedPages?.[requestedPage - 1];
	const firstPageData = loadedPages?.[0];
	const totalPages =
		firstPageData?.totalCount === undefined
			? undefined
			: pageSize > 0
				? Math.ceil(firstPageData.totalCount / pageSize)
				: 1;
	const requestedPageCanExist = totalPages === undefined || requestedPage <= totalPages;

	// The endpoint is cursor-paged while inbox URLs are numbered, so resolve missing intermediate pages in order.
	React.useEffect(() => {
		if (!enabled || requestedPageData || !requestedPageCanExist || !query.hasNextPage || query.isFetchingNextPage) {
			return;
		}
		void fetchNextPage();
	}, [
		enabled,
		loadedPageCount,
		fetchNextPage,
		query.hasNextPage,
		query.isFetchingNextPage,
		requestedPageCanExist,
		requestedPageData,
	]);

	const requestedPageError = query.isError || query.isFetchNextPageError;
	const requestedPageLoading =
		enabled &&
		!requestedPageData &&
		!requestedPageError &&
		(query.isLoading || query.isFetchingNextPage || (requestedPageCanExist && query.hasNextPage === true));
	const data =
		requestedPageData ??
		(firstPageData
			? {
					values: [],
					totalCount: firstPageData.totalCount,
				}
			: undefined);

	return {
		...query,
		data,
		isError: requestedPageError,
		isLoading: requestedPageLoading,
	};
}
